import React, { useState, useEffect } from 'react';
import { fetchDaoStats, fetchPopularDaos } from '../data/dao';

const SupportedDaos = () => {
  const [daos, setDaos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getDaoIcon = (daoId) => {
    const icons = {
      'aave.eth': '🏦',
      'uniswap': '🦄',
      'compound': '💱',
      'makerdao.eth': '🐱',
      'ens.eth': '🌐',
      'gitcoindao.eth': '💻',
      'balancer.eth': '⚖️',
      'curve.eth': '📈',
      'sushigov.eth': '🍣',
      'yearn': '🏦',
      'lido-snapshot.eth': '🪙',
      'arbitrum': '⚡',
      'optimism': '🔵'
    };
    return icons[daoId] || '🏛️';
  };

  useEffect(() => {
    const loadDaos = async () => {
      try {
        setLoading(true);
        
        // Fetch popular DAOs from the API
        const popularDaos = await fetchPopularDaos();
        
        console.log('Popular DAOs loaded:', popularDaos);

        // Fetch detailed stats for each DAO
        const daoData = await Promise.all(
          popularDaos.map(async (dao) => {
            try {
              const stats = await fetchDaoStats(dao.id);
              return {
                id: dao.id,
                name: dao.name || dao.id,
                icon: getDaoIcon(dao.id),
                members: stats?.totalMembers || 0,
                proposals: stats?.space?.proposalsCount || 0,
                activeProposals: stats?.activeProposals || 0,
                followers: stats?.space?.followersCount || 0,
                loaded: true,
                error: false
              };
            } catch (err) {
              console.error(`Failed to load data for ${dao.name}:`, err);
              return {
                id: dao.id,
                name: dao.name || dao.id,
                icon: getDaoIcon(dao.id),
                members: 0,
                proposals: 0,
                activeProposals: 0,
                followers: 0,
                loaded: false,
                error: true
              };
            }
          })
        );

        setDaos(daoData);
        setError(null);
      } catch (err) {
        setError('Failed to load DAO data');
        console.error('Error loading DAOs:', err);
      } finally {
        setLoading(false);
      }
    };

    loadDaos();
  }, []);

  const formatNumber = (num) => {
    if (!num && num !== 0) return 'N/A';
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-lg">
        <h3 className="text-xl font-bold text-dominant mb-4">Popular DAOs</h3>
        <div className="grid grid-cols-2 gap-3">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="animate-pulse flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
              <div className="w-6 h-6 bg-gray-200 rounded"></div>
              <div className="flex-1 space-y-1 min-w-0">
                <div className="h-3 bg-gray-200 rounded w-16"></div>
                <div className="h-2 bg-gray-200 rounded w-12"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-lg">
        <h3 className="text-xl font-bold text-dominant mb-4">Popular DAOs</h3>
        <div className="text-center text-red-600 py-4">
          <i className="fas fa-exclamation-triangle text-2xl mb-2"></i>
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-dominant transition-colors text-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-bold text-dominant">Popular DAOs</h3>
        <span className="text-xs bg-secondary text-white px-2 py-1 rounded">
          {daos.length} DAOs
        </span>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        {daos.map((dao, index) => (
          <div 
            key={dao.id} 
            className={`flex items-center gap-2 p-3 border rounded-lg transition-colors ${
              dao.error 
                ? 'border-red-200 bg-red-50' 
                : dao.loaded 
                ? 'border-gray-200 hover:border-secondary hover:shadow-sm cursor-pointer' 
                : 'border-yellow-200 bg-yellow-50'
            }`}
            onClick={() => {
              if (dao.loaded && !dao.error) {
                window.open(`https://snapshot.org/#/${dao.id}`, '_blank');
              }
            }}
          >
            <span className="text-lg flex-shrink-0">{dao.icon}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1 mb-1">
                <p className="font-medium text-sm truncate" title={dao.name}>
                  {dao.name}
                </p>
                {dao.error && (
                  <i className="fas fa-exclamation-triangle text-red-500 text-xs flex-shrink-0" title="Failed to load data"></i>
                )}
                {!dao.loaded && !dao.error && (
                  <i className="fas fa-sync text-yellow-500 text-xs animate-spin flex-shrink-0" title="Loading..."></i>
                )}
              </div>
              <div className="flex justify-between items-center text-xs text-gray-500">
                <span className="truncate mr-1" title={`${dao.members} members`}>
                  {formatNumber(dao.members)} members
                </span>
                <span className="truncate" title={`${dao.proposals} proposals`}>
                  {formatNumber(dao.proposals)} props
                </span>
              </div>
              {dao.activeProposals > 0 && (
                <div className="mt-1">
                  <span className="inline-block bg-green-100 text-green-800 text-xs px-1.5 py-0.5 rounded truncate">
                    {dao.activeProposals} active
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {/* Stats Summary */}
      {daos.some(dao => dao.loaded && !dao.error) && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Total Members:</span>
              <span className="font-medium ml-2">
                {formatNumber(daos.reduce((sum, dao) => sum + (dao.loaded ? dao.members : 0), 0))}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Total Proposals:</span>
              <span className="font-medium ml-2">
                {formatNumber(daos.reduce((sum, dao) => sum + (dao.loaded ? dao.proposals : 0), 0))}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Active Proposals:</span>
              <span className="font-medium text-green-600 ml-2">
                {daos.reduce((sum, dao) => sum + (dao.loaded ? dao.activeProposals : 0), 0)}
              </span>
            </div>
            <div>
              <span className="text-gray-600">DAOs Loaded:</span>
              <span className="font-medium ml-2">
                {daos.filter(dao => dao.loaded && !dao.error).length}/{daos.length}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Data Source Info */}
      <div className="mt-3 text-xs text-gray-500 text-center">
        <i className="fas fa-database mr-1"></i>
        Live data from Snapshot API
      </div>
    </div>
  );
};

export default SupportedDaos;